/**
 * Where each painted asset lives: category + id → `/art/<dir>/<file>.webp`.
 *
 * Owns: the id tables and the path rules for the art listed in
 * `design/art/manifest.json`. Must not: load anything or know how it is drawn
 * (`Painting.vue`, `RankBadge.vue`). `art-path.test.ts` reads the manifest and
 * asserts every path these tables can produce is a manifest row whose file exists,
 * so an id added here without art fails a test rather than a player's screen.
 */
import { RANKS } from '@shared/ranks';

const ART_ROOT = '/art';

export const RANK_IDS: readonly string[] = RANKS.map((r) => r.id);
export const CLASS_IDS = ['destroyer', 'cruiser', 'battleship'] as const;
export const ORDER_SLUGS = ['daily_sorties', 'daily_damage', 'daily_kills', 'daily_wins', 'weekly_wins', 'weekly_damage', 'weekly_kills'] as const;
export const SEASON_TIER_IDS = ['bronze', 'silver', 'gold', 'admiralty'] as const;
export const STATION_IDS = ['shipyard', 'research', 'shop', 'profile', 'papers', 'sea', 'office'] as const;
export const SCENE_IDS = ['victory', 'defeat', 'promotion', 'chandler', 'record', 'papers', 'office'] as const;
export const EMPTY_IDS = ['no_orders', 'no_notices', 'no_invoices', 'no_purchases'] as const;
export const PORTRAIT_IDS = [
  'destroyer_junior', 'destroyer_senior', 'destroyer_flag',
  'cruiser_junior', 'cruiser_senior', 'cruiser_flag',
  'battleship_junior', 'battleship_senior', 'battleship_flag',
] as const;

/**
 * The server's pack ids (`premium.data.ts`) → the manifest's file names. They differ for
 * `pack_supply` (art is `supply_run`) and the deal carries a `deal_` prefix.
 */
export const PACK_ART_FILE: Readonly<Record<string, string>> = {
  pack_ration: 'ration',
  pack_supply: 'supply_run',
  pack_convoy: 'convoy',
  pack_flotilla: 'flotilla',
  pack_armada: 'armada',
  deal_harbour_opening: 'harbour_opening',
};

const webp = (dir: string, file: string): string => `${ART_ROOT}/${dir}/${file}.webp`;

export const rankArt = (rankId: string): string => webp('ranks', rankId);
export const classArt = (cls: (typeof CLASS_IDS)[number]): string => webp('classes', cls);
export const seasonArt = (tier: (typeof SEASON_TIER_IDS)[number]): string => webp('season', tier);
export const stationArt = (id: (typeof STATION_IDS)[number]): string => webp('stations', id);
export const sceneArt = (id: (typeof SCENE_IDS)[number]): string => webp('scenes', id);
export const emptyArt = (id: (typeof EMPTY_IDS)[number]): string => webp('empty', id);
export const portraitArt = (id: (typeof PORTRAIT_IDS)[number]): string => webp('portraits', id);

/** An order's picture from its name key (`quests.<slug>.name`, what the results form is given), or null if it has none. */
export function orderArt(nameKey: string): string | null {
  const slug = /^quests\.([a-z_]+)\.name$/.exec(nameKey)?.[1];
  return slug && (ORDER_SLUGS as readonly string[]).includes(slug) ? webp('orders', slug) : null;
}

/** A pack or deal's picture from the server's id, or null for an id with no art (a custom amount). */
export function packArt(packId: string): string | null {
  const file = PACK_ART_FILE[packId];
  return file ? webp('packs', file) : null;
}

/** Every path the tables above can produce, for the manifest test. */
export function allArtPaths(): string[] {
  return [
    ...RANK_IDS.map(rankArt),
    ...CLASS_IDS.map(classArt),
    ...SEASON_TIER_IDS.map(seasonArt),
    ...STATION_IDS.map(stationArt),
    ...SCENE_IDS.map(sceneArt),
    ...EMPTY_IDS.map(emptyArt),
    ...PORTRAIT_IDS.map(portraitArt),
    ...ORDER_SLUGS.map((s) => orderArt(`quests.${s}.name`)!),
    ...Object.keys(PACK_ART_FILE).map((id) => packArt(id)!),
  ];
}
